// ═══════════════════════════════════════════════════════════
// Public Payment Page — /portal/pay/:token
//
// No login required. Requester follows a payment link emailed
// by staff. The token identifies the invoice and request.
// Payment processing is stubbed — no real card charges.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  IconLock, IconCheck, IconAlertCircle, IconLoader2,
  IconArrowLeft, IconCreditCard, IconReceipt,
} from '@tabler/icons-react';
import { validatePaymentToken, consumePaymentToken } from '../../api/paymentTokens';
import { getInvoiceForRequest, processPayment, type Invoice } from '../../api/invoices';
import { formatMoney, formatDate } from '../../lib/format';

type PageState = 'loading' | 'invalid' | 'form' | 'processing' | 'success' | 'already-paid';

// ── Card helpers ─────────────────────────────────────────

function detectBrand(num: string): string {
  const n = num.replace(/\s/g, '');
  if (n.startsWith('4')) return 'Visa';
  if (/^5[1-5]/.test(n)) return 'Mastercard';
  if (/^3[47]/.test(n)) return 'Amex';
  if (n.startsWith('6')) return 'Discover';
  return 'Card';
}

function formatCardNumber(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 16);
  return digits.replace(/(.{4})/g, '$1 ').trim();
}

function formatExpiry(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length > 2) return digits.slice(0, 2) + '/' + digits.slice(2);
  return digits;
}

function validateForm(form: { cardNumber: string; expiry: string; cvv: string; name: string }): string | null {
  const digits = form.cardNumber.replace(/\s/g, '');
  if (digits.length < 15) return 'Card number is incomplete.';
  const [mm, yy] = form.expiry.split('/');
  const month = parseInt(mm, 10);
  const year  = 2000 + parseInt(yy ?? '0', 10);
  if (!mm || !yy || month < 1 || month > 12) return 'Invalid expiry date.';
  const expDate = new Date(year, month - 1, 1);
  if (expDate < new Date()) return 'Card has expired.';
  if (form.cvv.length < 3) return 'CVV is incomplete.';
  if (!form.name.trim()) return 'Cardholder name is required.';
  return null;
}

// ── Sub-components ───────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-600)' }}>{label}</label>
      {children}
    </div>
  );
}

// ── Main component ───────────────────────────────────────

export default function PaymentPage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState]     = useState<PageState>('loading');
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [tokenData, setTokenData] = useState<{ recordId: number; requestId: number; invoiceId: number } | null>(null);
  const [txRef, setTxRef]     = useState('');
  const [error, setError]     = useState<string | null>(null);

  const [form, setForm] = useState({
    cardNumber: '',
    expiry:     '',
    cvv:        '',
    name:       '',
  });

  useEffect(() => {
    if (!token) { setState('invalid'); return; }
    (async () => {
      const validated = await validatePaymentToken(token);
      if (!validated) { setState('invalid'); return; }

      const inv = await getInvoiceForRequest(validated.requestId, { anonymous: true });
      if (!inv) { setState('invalid'); return; }

      if (inv.status === 'paid') {
        setInvoice(inv);
        setState('already-paid');
        return;
      }

      setTokenData(validated);
      setInvoice(inv);
      setState('form');
    })();
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const err = validateForm(form);
    if (err) { setError(err); return; }
    setError(null);
    setState('processing');

    // Simulate processing delay
    await new Promise(r => setTimeout(r, 2200));

    try {
      const digits = form.cardNumber.replace(/\s/g, '');
      const last4  = digits.slice(-4);
      const brand  = detectBrand(form.cardNumber);

      await processPayment(
        tokenData!.invoiceId,
        tokenData!.requestId,
        brand,
        last4,
        { anonymous: true },
      );

      await consumePaymentToken(tokenData!.recordId);

      // Re-fetch invoice for receipt data
      const updated = await getInvoiceForRequest(tokenData!.requestId, { anonymous: true });
      setInvoice(updated);
      setTxRef(updated?.stripePaymentId ?? '');
      setState('success');
    } catch {
      setError('Payment could not be processed. Please try again.');
      setState('form');
    }
  }

  // ── Shell wrapper ──────────────────────────────────────

  const shell = (content: React.ReactNode) => (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', padding: '48px 16px' }}>
      <div style={{ width: '100%', maxWidth: 520 }}>
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconCreditCard size={18} style={{ color: '#fff' }} />
          </div>
          <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--color-neutral-900)' }}>Secure Payment</span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--color-neutral-400)' }}>
            <IconLock size={11} /> SSL Secured
          </div>
        </div>
        {content}
      </div>
    </div>
  );

  // ── States ─────────────────────────────────────────────

  if (state === 'loading') return shell(
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--color-neutral-500)', fontSize: 14 }}>
      <IconLoader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Verifying payment link…
    </div>
  );

  if (state === 'invalid') return shell(
    <div style={{ background: '#fff', border: '1px solid var(--color-neutral-200)', borderRadius: 12, padding: 32, textAlign: 'center' }}>
      <IconAlertCircle size={36} style={{ color: 'var(--cds-red-700)', marginBottom: 12 }} />
      <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Link Invalid or Expired</div>
      <div style={{ fontSize: 13, color: 'var(--color-neutral-500)', marginBottom: 24 }}>
        This payment link is no longer valid. Payment links expire after 30 days. Please contact the records office for a new link.
      </div>
      <Link to="/portal" style={{ fontSize: 13, color: 'var(--color-primary)' }}>← Return to Portal</Link>
    </div>
  );

  if (state === 'already-paid') return shell(
    <div style={{ background: '#fff', border: '1px solid var(--color-neutral-200)', borderRadius: 12, padding: 32, textAlign: 'center' }}>
      <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--cds-green-50)', border: '2px solid var(--cds-green-200)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
        <IconCheck size={28} style={{ color: 'var(--cds-green-700)' }} />
      </div>
      <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-neutral-900)', marginBottom: 8 }}>Already Paid</div>
      <div style={{ fontSize: 13, color: 'var(--color-neutral-500)', marginBottom: 4 }}>
        This invoice was paid on {invoice?.paidAt ? formatDate(invoice.paidAt) : '—'}.
      </div>
      {invoice?.paymentMethod && (
        <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginBottom: 24 }}>
          {invoice.paymentMethod}
        </div>
      )}
      <Link to="/portal/track" style={{ fontSize: 13, color: 'var(--color-primary)' }}>← Track your request</Link>
    </div>
  );

  if (state === 'success') return shell(
    <div style={{ background: '#fff', border: '1px solid var(--color-neutral-200)', borderRadius: 12, overflow: 'hidden' }}>
      {/* Success header */}
      <div style={{ padding: '32px 32px 24px', textAlign: 'center', borderBottom: '1px solid var(--color-neutral-100)' }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: 'var(--cds-green-50)', border: '2px solid var(--cds-green-200)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
        }}>
          <IconCheck size={32} style={{ color: 'var(--cds-green-700)' }} />
        </div>
        <div style={{ fontWeight: 800, fontSize: 20, color: 'var(--color-neutral-900)', marginBottom: 6 }}>
          Payment Confirmed
        </div>
        <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--cds-green-700)', marginBottom: 4 }}>
          {invoice ? formatMoney(invoice.total) : ''}
        </div>
        <div style={{ fontSize: 13, color: 'var(--color-neutral-400)' }}>
          {invoice?.paidAt ? formatDate(invoice.paidAt) : ''}
        </div>
      </div>

      {/* Receipt details */}
      <div style={{ padding: '20px 32px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-neutral-400)', marginBottom: 12 }}>
          Receipt
        </div>
        {[
          ['Payment method', invoice?.paymentMethod ?? '—'],
          ['Transaction reference', txRef || invoice?.stripePaymentId || '—'],
          ['Status', 'Paid in full'],
        ].map(([label, value]) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '8px 0', borderBottom: '1px solid var(--color-neutral-100)', fontSize: 13 }}>
            <span style={{ color: 'var(--color-neutral-500)' }}>{label}</span>
            <span style={{ fontWeight: 600, color: 'var(--color-neutral-800)' }}>{value}</span>
          </div>
        ))}

        <div style={{ marginTop: 20, padding: '14px 16px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, fontSize: 12, color: 'var(--cds-blurple-700)', lineHeight: 1.6 }}>
          <IconReceipt size={13} style={{ verticalAlign: 'middle', marginRight: 5 }} />
          Your request is now under review. You will receive an email when your records are ready.
        </div>

        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center' }}>
          <Link to="/portal/track" style={{ fontSize: 13, color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <IconArrowLeft size={13} /> Track your request
          </Link>
        </div>
      </div>
    </div>
  );

  // ── Payment form ───────────────────────────────────────

  const brand   = detectBrand(form.cardNumber);
  const digits  = form.cardNumber.replace(/\s/g, '');
  const isAmex  = brand === 'Amex';

  const brandColors: Record<string, string> = {
    Visa: '#1A1F71', Mastercard: '#EB001B', Amex: '#007BC1', Discover: '#FF6600',
  };
  const brandColor = brandColors[brand] ?? 'var(--color-neutral-400)';

  return shell(
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Order summary */}
      {invoice && (
        <div style={{ background: '#fff', border: '1px solid var(--color-neutral-200)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--color-neutral-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-500)' }}>
              Invoice #{invoice.recordId}
            </span>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-neutral-900)' }}>
              {formatMoney(invoice.total)}
            </span>
          </div>
          {invoice.items.map(item => (
            <div key={item.recordId} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 20px', borderBottom: '1px solid var(--color-neutral-100)', fontSize: 13 }}>
              <span style={{ color: 'var(--color-neutral-600)' }}>
                {item.description}
                {item.quantity !== 1 && <span style={{ color: 'var(--color-neutral-400)', marginLeft: 5 }}>×{item.quantity}</span>}
              </span>
              <span style={{ fontWeight: 500, color: 'var(--color-neutral-800)' }}>{formatMoney(item.total)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 20px', fontSize: 15, fontWeight: 700, color: 'var(--color-neutral-900)' }}>
            <span>Total due</span>
            <span>{formatMoney(invoice.total)}</span>
          </div>
        </div>
      )}

      {/* Card form */}
      <form onSubmit={handleSubmit} style={{ background: '#fff', border: '1px solid var(--color-neutral-200)', borderRadius: 12, padding: '24px 24px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-neutral-900)', marginBottom: 4 }}>
          Card details
        </div>

        {/* Card number */}
        <Field label="Card number">
          <div style={{ position: 'relative' }}>
            <input
              className="form-input"
              type="text"
              inputMode="numeric"
              autoComplete="cc-number"
              placeholder="1234 5678 9012 3456"
              value={form.cardNumber}
              maxLength={19}
              style={{ paddingRight: 64, fontFamily: 'monospace', fontSize: 15, letterSpacing: '0.05em' }}
              onChange={e => setForm(f => ({ ...f, cardNumber: formatCardNumber(e.target.value) }))}
            />
            {digits.length > 0 && (
              <span style={{
                position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                fontSize: 11, fontWeight: 700, color: brandColor, letterSpacing: '0.04em',
              }}>
                {brand.toUpperCase()}
              </span>
            )}
          </div>
        </Field>

        {/* Expiry + CVV */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Expiry">
            <input
              className="form-input"
              type="text"
              inputMode="numeric"
              autoComplete="cc-exp"
              placeholder="MM / YY"
              value={form.expiry}
              maxLength={5}
              style={{ fontFamily: 'monospace', fontSize: 14, letterSpacing: '0.05em' }}
              onChange={e => setForm(f => ({ ...f, expiry: formatExpiry(e.target.value) }))}
            />
          </Field>
          <Field label={isAmex ? 'CID (4 digits)' : 'CVV'}>
            <input
              className="form-input"
              type="text"
              inputMode="numeric"
              autoComplete="cc-csc"
              placeholder={isAmex ? '••••' : '•••'}
              value={form.cvv}
              maxLength={isAmex ? 4 : 3}
              style={{ fontFamily: 'monospace', fontSize: 14, letterSpacing: '0.1em' }}
              onChange={e => setForm(f => ({ ...f, cvv: e.target.value.replace(/\D/g, '').slice(0, isAmex ? 4 : 3) }))}
            />
          </Field>
        </div>

        {/* Cardholder name */}
        <Field label="Name on card">
          <input
            className="form-input"
            type="text"
            autoComplete="cc-name"
            placeholder="Full name as printed on card"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            style={{ fontSize: 14 }}
          />
        </Field>

        {/* Error */}
        {error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--cds-red-50)', border: '1px solid var(--cds-red-200)', borderRadius: 8, fontSize: 13, color: 'var(--cds-red-700)' }}>
            <IconAlertCircle size={14} style={{ flexShrink: 0 }} /> {error}
          </div>
        )}

        {/* Security note */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--color-neutral-400)' }}>
          <IconLock size={11} />
          Your payment information is encrypted and secure. We do not store your card details.
        </div>

        {/* Submit */}
        <button
          type="submit"
          className="btn btn-primary"
          style={{ fontSize: 15, fontWeight: 700, padding: '12px 0', borderRadius: 8, gap: 8 }}
          disabled={state === 'processing'}
        >
          {state === 'processing'
            ? <><IconLoader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Processing…</>
            : invoice ? `Pay ${formatMoney(invoice.total)}` : 'Pay'
          }
        </button>
      </form>

      {state === 'processing' && (
        <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--color-neutral-400)', padding: '4px 0' }}>
          Processing your payment — please do not close this window.
        </div>
      )}

      <div style={{ textAlign: 'center' }}>
        <Link to="/portal" style={{ fontSize: 12, color: 'var(--color-neutral-400)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <IconArrowLeft size={12} /> Back to portal
        </Link>
      </div>
    </div>
  );
}
